/**
 * Winter upsell opportunity — the camera that pops up after Complete.
 *
 * Owner, Oct 27 2026: "for the crew after complete job, super easy to deny, it'll just pop up a camera option that
 * says winter upsell opportunity, and they can click a photo of trees or bushes or fence repair, anything really."
 * Same day: "allow multiple pictures for the upsell."
 *
 * A few photos, no words needed. The first tap opens the camera; each shot lands as a thumbnail with its own X, and
 * "Add another" takes one more (up to MAX_PHOTOS). "Send" posts them all at once to POST /jobs/:id/upsell-photo,
 * which keeps every one, puts ONE reminder on the customer for today and texts Michael all the pictures on one text.
 * No signal: the photos wait on the phone with the other queued photos, one record each, and the server lands them
 * on the same reminder when they arrive. "No thanks" (or a tap outside) closes it and nothing is recorded.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Camera, Plus, X } from 'lucide-react';
import { api } from '../../services/api';
import { shrinkPhoto } from '../../utils/image';
import { isRetryableUploadError } from '../../hooks/useOfflinePhotoQueue';

export type EnqueueUpsell = (file: File, opts: { kind: 'upsell' }, force?: boolean) => Promise<boolean>;

/** How many photos one send may carry — the same cap the server keeps (services/crewNoteAlert.MAX_PHOTOS). */
export const MAX_PHOTOS = 6;

interface Shot { id: number; file: File; url: string }

export default function WinterUpsellPrompt({ jobId, customerName, onClose, enqueueOffline }: {
  jobId: number | string;
  customerName?: string | null;
  onClose: () => void;
  enqueueOffline?: EnqueueUpsell;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [shots, setShots] = useState<Shot[]>([]);
  const nextId = useRef(1);

  // The thumbnails' object URLs are freed when the sheet goes.
  const shotsRef = useRef(shots);
  shotsRef.current = shots;
  useEffect(() => () => { for (const s of shotsRef.current) URL.revokeObjectURL(s.url); }, []);

  const full = shots.length >= MAX_PHOTOS;

  /** The camera handed back one or more pictures: shrink each and add it to the row, up to the cap. */
  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const room = MAX_PHOTOS - shots.length;
    if (room <= 0) { toast.info(`Up to ${MAX_PHOTOS} photos — send these first`); return; }
    setBusy(true);
    try {
      const added: Shot[] = [];
      for (const f of files.slice(0, room)) {
        let small: File = f;
        try { small = await shrinkPhoto(f); } catch { /* send it as it is */ }
        added.push({ id: nextId.current++, file: small, url: URL.createObjectURL(small) });
      }
      setShots(prev => [...prev, ...added]);
      if (files.length > room) toast.info(`Only ${MAX_PHOTOS} photos at a time — the first ${room} were kept`);
    } finally { setBusy(false); }
  }

  function remove(id: number) {
    setShots(prev => {
      const gone = prev.find(s => s.id === id);
      if (gone) URL.revokeObjectURL(gone.url);
      return prev.filter(s => s.id !== id);
    });
  }

  /** Queue every photo on the phone (one record each; the server lands them on one reminder). */
  async function keepAll(force?: boolean): Promise<boolean> {
    if (!enqueueOffline) return false;
    let kept = 0;
    for (const s of shots) { if (await enqueueOffline(s.file, { kind: 'upsell' }, force)) kept++; }
    return kept === shots.length;
  }

  async function send() {
    if (!shots.length || busy) return;
    setBusy(true);
    try {
      if (!navigator.onLine) {
        if (await keepAll()) { onClose(); return; }
        toast.error('No signal — try again when you are back in range');
        return;
      }
      const form = new FormData();
      for (const s of shots) form.append('photos', s.file, s.file.name);
      const r = await api.post(`/jobs/${jobId}/upsell-photo`, form);
      toast.success(r.data?.message || 'Sent — thanks for the heads-up');
      onClose();
    } catch (err: any) {
      if (isRetryableUploadError(err) && await keepAll(true)) { onClose(); return; }
      toast.error(err.response?.data?.message || 'Could not send the photos');
    } finally { setBusy(false); }
  }

  const n = shots.length;
  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={() => !busy && onClose()}>
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">Winter upsell opportunity</p>
            <h3 className="mt-1 text-lg font-bold text-gray-900">See anything that could use work{customerName ? ` at ${customerName}'s` : ''}?</h3>
            <p className="mt-1 text-sm text-gray-600">Trees, bushes, a leaning fence, a bare bed — anything. Snap a few photos and Michael gets them. No words needed.</p>
          </div>
          <button type="button" onClick={onClose} disabled={busy} aria-label="No thanks" className="p-1.5 rounded-full text-gray-400 active:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>
        <input ref={ref} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={pick} />

        {n > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-2">
            {shots.map(s => (
              <div key={s.id} className="relative aspect-square">
                <img src={s.url} alt="" className="h-full w-full rounded-xl object-cover border border-gray-200" />
                <button type="button" disabled={busy} onClick={() => remove(s.id)} aria-label="Remove this photo"
                  className="absolute -top-2 -right-2 h-8 w-8 rounded-full bg-gray-900 text-white flex items-center justify-center shadow">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
            {!full && (
              <button type="button" disabled={busy} onClick={() => ref.current?.click()} aria-label="Add another photo"
                className="aspect-square rounded-xl border-2 border-dashed border-emerald-300 text-emerald-700 flex flex-col items-center justify-center text-xs font-semibold">
                <Plus className="h-6 w-6" /> Add another
              </button>
            )}
          </div>
        )}
        {full && <p className="mt-2 text-xs text-gray-500">That's the most for one send ({MAX_PHOTOS}).</p>}

        {n === 0 ? (
          <button type="button" disabled={busy} onClick={() => ref.current?.click()}
            className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl bg-emerald-600 text-white font-semibold py-3.5 text-base disabled:opacity-60">
            <Camera className="h-5 w-5" /> {busy ? 'One moment…' : 'Take a photo'}
          </button>
        ) : (
          <button type="button" disabled={busy} onClick={send}
            className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl bg-emerald-600 text-white font-semibold py-3.5 text-base disabled:opacity-60">
            {busy ? 'Sending…' : `Send ${n === 1 ? 'photo' : `${n} photos`} to Michael`}
          </button>
        )}
        <button type="button" disabled={busy} onClick={onClose}
          className="mt-2 w-full rounded-xl border border-gray-200 text-gray-600 font-medium py-3 text-base">
          No thanks
        </button>
      </div>
    </div>
  );
}
